<template>
    <section
        :id="slice.primary.anchorName || undefined"
        ref="section"
        class="slice slice-stepper slice-stepper--default"
        aria-label="stepper"
        data-testid="slice-stepper-default"
    >
        <div class="slice-content">
            <PrismicRichText :field="slice.primary.title" class="slice-title" data-testid="slice-stepper-title" />
            <div class="slice-body slice-stepper__steps">
                <div
                    v-for="(item, i) in slice.items"
                    :key="i"
                    class="slice-stepper__step"
                    :class="{
                        'slice-stepper__step--active': mounted,
                        'slice-stepper__step--theme-malt': theme === 'malt',
                    }"
                    :data-testid="`slice-stepper-step--${i}`"
                    :data-theme="theme === 'malt' ? 'malt' : undefined"
                >
                    <div
                        v-if="i > 0"
                        class="slice-stepper__step__line"
                        :style="{transitionDuration: `${transitionDuration}s`, transitionDelay: `${i * transitionDuration}s`}"
                    />
                    <div class="slice-stepper__step__content">
                        <div class="slice-stepper__step__circle" />
                        <div class="slice-stepper__step__number" :data-testid="`slice-stepper-step-number--${i}`">
                            {{ i + 1 }}
                        </div>
                        <div class="slice-stepper__step__title" :data-testid="`slice-stepper-step-title--${i}`">{{ item.title }}</div>
                        <PrismicRichText :field="item.text" class="slice-stepper__step__text" :data-testid="`slice-stepper-step-text--${i}`" />
                    </div>
                </div>
            </div>
            <div v-if="ctaUrl || ctaLink.url" class="slice-footer">
                <VJoyButton
                    :href="$withLocalePrefix(ctaUrl || ctaLink.url)"
                    :variant="buttonVariants"
                    data-component-source="stepper"
                    data-testid="slice-stepper-cta"
                >
                    {{ ctaLabel }}
                </VJoyButton>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, onMounted, ref, useTemplateRef} from 'vue';
    import type {FilledLinkToWebField} from '@prismicio/types';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {StepperSlice} from '@prismicio-types';
    import {useComponentTracking} from '#imports';
    import {VJoyButton, type TJoyButtonVariants} from '@maltjoy/core-vue';

    type StepperDefaultVariation = Extract<StepperSlice, {variation: 'default'}>;

    const props = defineProps(getSliceComponentProps<StepperDefaultVariation>());

    const mounted = ref(false);
    const transitionDuration = 0.5;

    onMounted(() => {
        mounted.value = true;
    });

    const theme = computed(() => props.slice.primary.theme ?? 'default');
    const buttonVariants = computed<TJoyButtonVariants>(() => (theme.value === 'malt' ? 'main' : 'primary'));
    const ctaLink = computed(() => props.slice.primary.ctaLink as FilledLinkToWebField);
    const ctaUrl = computed(() => props.slice.primary.ctaUrl);
    const ctaLabel = computed(() => props.slice.primary.ctaLabel);

    const section = useTemplateRef('section');

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    $step-line-y: calc(var(--joy-core-spacing-8) + var(--joy-core-spacing-3) - 1px);
    $step-circle-transition: all 1s cubic-bezier(0, 0, 0.2, 1);

    .slice-stepper {
        &__steps {
            position: relative;
            overflow: hidden;

            @include mq.screen_md(min) {
                display: flex;
            }
        }

        &__step {
            position: relative;
            max-width: 450px;
            margin: 0 auto;

            @include mq.screen_md(min) {
                flex: none;
                width: 33.33%;

                &__content {
                    margin: 0 var(--joy-core-spacing-4);
                }

                &:first-child &__content {
                    margin-left: 0;
                }

                &:last-child &__content {
                    margin-right: 0;
                }
            }

            &__number {
                margin: 0 auto;
                color: var(--joy-color-secondary-30);
                font-family: var(--joy-font-family-title);
                font-size: var(--joy-font-size-secondary-600);
                text-align: center;
            }

            &__title {
                font-size: var(--joy-font-size-primary-800);
                font-weight: var(--joy-font-weight-bold);
            }

            &__text {
                margin-top: var(--joy-core-spacing-4);
                color: var(--joy-color-neutral-40);
            }

            &__circle {
                opacity: 0;
                width: var(--joy-core-spacing-6);
                height: var(--joy-core-spacing-6);
                border-radius: var(--joy-core-radius-6);
                background-color: var(--joy-color-secondary-30);
                transition: $step-circle-transition;
                margin: var(--joy-core-spacing-12) auto var(--joy-core-spacing-3);

                @include mq.screen_md(min) {
                    margin: var(--joy-core-spacing-8) auto;
                }
            }

            &--active &__circle {
                transform: scale(1);
                opacity: 1;
            }

            &__line {
                position: absolute;
                z-index: -1;
                background-color: var(--joy-color-tertiary-70);
                transform-origin: 0 0;
                transition: transform 1s linear;
                left: 50%;
                top: calc(-1 * var(--joy-core-spacing-11));
                height: var(--joy-core-spacing-11);
                width: 1px;

                @include mq.screen_md(min) {
                    width: auto;
                    height: 1px;
                    top: $step-line-y;
                    left: -50%;
                    right: 50%;
                    transform: scaleX(0);
                }
            }

            &--active &__line {
                transform: scaleX(1);
            }

            &--theme-malt &__number {
                color: var(--joy-color-primary-50);
            }

            &--theme-malt &__circle {
                background-color: var(--joy-color-primary-50);
            }

            &--theme-malt &__line {
                background-color: var(--joy-color-primary-50);
            }
        }
    }
</style>
